// Custom confirmation modal utility — replaces browser-native window.confirm()
// Uses the existing gl-modal design system with Bootstrap 5 modal API.
// Dynamically creates/destroys modal DOM so no layout partial is needed.
//
// Design follows the React AlertDialog / ConfirmDialog modal shell:
//   - gl-modal content with gl-modal-header / gl-modal-body / gl-modal-footer slots
//   - Intent header bar
//   - Plain gl-modal body text
//   - Action-specific button labels with icons
//
// The shell itself — skeleton markup, stacking z-index, the Bootstrap instance,
// listener bookkeeping, teardown, and the stacked-backdrop bump — lives in
// lib/gl_modal_shell.js. Each helper below is a declarative config over it, so
// this file owns only what makes a confirm / alert / prompt / acknowledgement
// dialog different from the others.

import { escapeHTML, mountGlModalShell } from './gl_modal_shell'

// ---------------------------------------------------------------------------
// Action detection — maps message text to variant, verb, icon, and title
// ---------------------------------------------------------------------------

const ACTION_PATTERNS = [
  // Destructive actions (red)
  { regex: /\bdelete\b/i,       variant: 'danger',  verb: 'Delete',       btnIcon: 'ph ph-trash' },
  { regex: /\bremove\b/i,       variant: 'danger',  verb: 'Remove',       btnIcon: 'ph ph-x-circle' },
  { regex: /\babandon\b/i,      variant: 'danger',  verb: 'Abandon',      btnIcon: 'ph ph-x-circle' },
  { regex: /\bdestroy\b/i,      variant: 'danger',  verb: 'Delete',       btnIcon: 'ph ph-trash' },
  { regex: /\bcancel\b/i,       variant: 'danger',  verb: 'Yes, Cancel',  btnIcon: 'ph ph-x-circle' },
  { regex: /\block\b/i,         variant: 'danger',  verb: 'Lock',         btnIcon: 'ph ph-lock' },
  { regex: /\bdisconnect\b/i,   variant: 'danger',  verb: 'Disconnect',   btnIcon: 'ph ph-plug' },
  { regex: /\brevoke\b/i,       variant: 'danger',  verb: 'Revoke',       btnIcon: 'ph ph-shield-warning' },
  { regex: /\bdeny\b/i,         variant: 'danger',  verb: 'Deny',         btnIcon: 'ph ph-x-circle' },
  // Warning actions (yellow/orange)
  { regex: /\boverride\b/i,     variant: 'warning', verb: 'Override',     btnIcon: 'ph ph-warning' },
  { regex: /\brevert\b/i,       variant: 'warning', verb: 'Revert',       btnIcon: 'ph ph-arrow-counter-clockwise' },
  { regex: /\bdangerous\b/i,    variant: 'warning', verb: 'Confirm',      btnIcon: 'ph ph-warning' },
  { regex: /\bwarning\b/i,      variant: 'warning', verb: 'Confirm',      btnIcon: 'ph ph-warning' },
  { regex: /\bpause\b/i,        variant: 'warning', verb: 'Pause',        btnIcon: 'ph ph-pause-circle' },
  // Success/positive actions (green)
  { regex: /\bapprove\b/i,      variant: 'success', verb: 'Approve',      btnIcon: 'ph ph-check-circle' },
  { regex: /\bunlock\b/i,       variant: 'success', verb: 'Unlock',       btnIcon: 'ph ph-lock-open' },
  { regex: /\bresume\b/i,       variant: 'success', verb: 'Resume',       btnIcon: 'ph ph-play-circle' },
  { regex: /\bverify\b/i,       variant: 'success', verb: 'Verify',       btnIcon: 'ph ph-check-circle' },
  // Neutral actions (brand primary)
  { regex: /\bexport\b/i,       variant: 'primary', verb: 'Export',       btnIcon: 'ph ph-download-simple' },
  { regex: /\bcontinue\b/i,     variant: 'primary', verb: 'Continue',     btnIcon: 'ph ph-arrow-right' },
  { regex: /\bproceed\b/i,      variant: 'primary', verb: 'Proceed',      btnIcon: 'ph ph-arrow-right' },
  { regex: /\bassign\b/i,       variant: 'primary', verb: 'Assign',       btnIcon: 'ph ph-user-check' },
]

function detectAction(message) {
  for (const pattern of ACTION_PATTERNS) {
    if (pattern.regex.test(message)) return pattern
  }
  return { variant: 'primary', verb: 'Confirm', btnIcon: 'ph ph-check' }
}

// ---------------------------------------------------------------------------
// Variant config — header tone, header glyphs, and the two button roles
// ---------------------------------------------------------------------------
//
// Mirrors the React intent table (app/frontend/react/components/shared/
// dialogIntent.ts) so the two dialog families never drift on tone or glyph:
//   headerIcon  the question-shaped glyph a CONFIRM / PROMPT / ACKNOWLEDGEMENT
//               header carries (it asks the user something)
//   alertIcon   the statement-shaped glyph an ALERT header carries (it tells
//               the user something) — differs only on the neutral variant
// Every header glyph is the Fill weight (MODAL-1, TOKEN-4): the header icon is
// an action icon, never secondary chrome.
//
// Two button roles, because `gl-btn-warning` is sanctioned only as the
// CAUTIONARY ACTION tier (design-standards → Buttons: "cautionary
// gl-btn-warning"), and its white-on-amber label measures 2.15:1 in the light
// theme (#13336, A11Y-4):
//   actionClass the button that PERFORMS the cautionary / destructive /
//               positive action the message names — a confirm's Override,
//               Revert, Pause, the acknowledgement's Continue. Keeps the
//               semantic tier; the #13740 alternate-button shape depends on
//               the confirm NOT being a second gl-btn-primary.
//   okClass     the button that merely acknowledges (alert OK) or submits a
//               value (prompt OK). A one-button modal's action is the
//               canonical main-action tier, gl-btn-primary — so the warning
//               variant hands these slots to the primary tier. Danger keeps
//               its destructive tier on both roles (its light ratio is the
//               shared-token issue #13337); success clears AA since #13102.

const VARIANT_CONFIG = {
  danger: {
    headerIcon: 'ph-fill ph-warning-octagon',
    alertIcon: 'ph-fill ph-warning-octagon',
    actionClass: 'gl-btn gl-btn-danger',
    okClass: 'gl-btn gl-btn-danger',
    headerClass: 'gl-modal-header--danger',
  },
  warning: {
    headerIcon: 'ph-fill ph-warning',
    alertIcon: 'ph-fill ph-warning',
    actionClass: 'gl-btn gl-btn-warning',
    okClass: 'gl-btn gl-btn-primary',
    headerClass: 'gl-modal-header--warning',
  },
  success: {
    headerIcon: 'ph-fill ph-check-circle',
    alertIcon: 'ph-fill ph-check-circle',
    actionClass: 'gl-btn gl-btn-success',
    okClass: 'gl-btn gl-btn-success',
    headerClass: 'gl-modal-header--success',
  },
  primary: {
    headerIcon: 'ph-fill ph-question',
    alertIcon: 'ph-fill ph-info',
    actionClass: 'gl-btn gl-btn-primary',
    okClass: 'gl-btn gl-btn-primary',
    headerClass: 'gl-modal-header--dark',
  },
}

// Every dialog here shares the same cancel affordance.
function cancelButton(cancelText, className) {
  return {
    ref: 'cancel',
    text: cancelText,
    className: className || 'gl-btn gl-btn-outline',
    dismiss: true,
  }
}

function messageBody(message, options = {}) {
  const lead = `<p class="gl-modal-text mb-0">${escapeHTML(message)}</p>`
  // An optional second paragraph that may carry ONE link. Text is escaped;
  // only the href is trusted — callers pass a Rails route helper, never input.
  const { linkHref, linkText, detail } = options
  if (!detail) return lead

  const before = escapeHTML(detail.before || '')
  const after = escapeHTML(detail.after || '')
  const anchor = linkHref
    ? `<a href="${escapeHTML(linkHref)}" target="_blank" rel="noopener">${escapeHTML(linkText || linkHref)}</a>`
    : escapeHTML(linkText || '')
  return `${lead}<p class="gl-modal-text mt-2 mb-0">${before}${anchor}${after}</p>`
}

function checkboxMarkup(checkboxId, label, extraClass = '') {
  return `
              <div class="gl-form-check${extraClass ? ` ${extraClass}` : ''}">
                <input type="checkbox" class="gl-form-check-input" id="${checkboxId}">
                <label class="gl-form-check-label" for="${checkboxId}">${escapeHTML(label || '')}</label>
              </div>`
}

/**
 * Show a styled confirmation modal.
 *
 * @param {string} message - The confirmation message to display
 * @param {Object} [options] - Optional overrides
 * @param {string} [options.title] - Modal title (auto-detected if omitted)
 * @param {string} [options.confirmText] - Confirm button label (auto-detected if omitted)
 * @param {string} [options.cancelText='Cancel'] - Cancel button label
 * @param {string} [options.variant] - 'danger' | 'warning' | 'success' | 'primary' (auto-detected)
 * @param {string} [options.icon] - Phosphor icon class for header (auto-detected)
 * @param {string} [options.headerClass] - Extra CSS class for the header
 * @param {string} [options.confirmClass] - Override the confirm button class
 *   (defaults to the variant's button class). Use to recolor only the button —
 *   e.g. a red "Update" on an otherwise-neutral primary dialog — without
 *   pulling in the full danger header + alert banner.
 * @param {{before?: string, after?: string}} [options.detail] - Optional second
 *   paragraph under the message, for "here is how to fix it" copy. Rendered as
 *   `before` + link/`linkText` + `after`, every part escaped.
 * @param {string} [options.linkHref] - href for the link inside `detail`. Pass a
 *   route helper's output only; when omitted, `linkText` renders as plain text.
 * @param {string} [options.linkText] - visible text of the link inside `detail`
 * @param {{text: string, className?: string, icon?: string}} [options.alternate] -
 *   Optional third button between Cancel and Confirm for a "do the better
 *   thing instead" path (e.g. "Use my profile days" on the one-delivery-day
 *   prompt, #13740). Rendered as the recommended primary action; the confirm
 *   button keeps the variant's colour. Resolves the string 'alternate'.
 * @returns {Promise<boolean|'alternate'>} Resolves true if confirmed, false if
 *   cancelled, or 'alternate' when `options.alternate` was chosen
 */
export function showConfirmModal(message, options = {}) {
  const action = detectAction(message)
  const variant = options.variant || action.variant
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.primary

  return new Promise((resolve) => {
    let outcome = false

    const cancel = cancelButton(options.cancelText || 'Cancel', options.cancelClass)
    if (options.focusRef === 'cancel') cancel.autofocus = true
    const confirm = {
      ref: 'confirm',
      text: options.confirmText || action.verb,
      className: options.confirmClass || config.actionClass,
      icon: action.btnIcon,
      onClick: (shell) => {
        outcome = true
        shell.hide()
      },
    }
    const alternate = options.alternate?.text
      ? {
        ref: 'alternate',
        text: options.alternate.text,
        className: options.alternate.className || 'gl-btn gl-btn-primary',
        icon: options.alternate.icon,
        onClick: (shell) => {
          outcome = 'alternate'
          shell.hide()
        },
      }
      : null
    const buttons = [cancel]
    if (alternate) buttons.push(alternate)
    buttons.push(confirm)
    // confirmFirst puts the confirming action on the left and the cancel
    // (the safe default) on the right, where focusRef can land on it.
    const ordered = options.confirmFirst
      ? [confirm, ...(alternate ? [alternate] : []), cancel]
      : buttons

    mountGlModalShell({
      idPrefix: 'custom-confirm-modal',
      title: options.title || action.verb,
      headerIcon: options.icon || config.headerIcon,
      headerClass: options.headerClass || config.headerClass,
      bodyHTML: options.bodyHTML || messageBody(message, options),
      buttons: ordered,
      scrollable: options.scrollable || false,
      onShown: options.focusRef
        ? (shell) => { shell.buttons[options.focusRef]?.focus() }
        : undefined,
      onHidden: () => resolve(outcome),
    })
  })
}

/**
 * Show a styled alert modal (informational, single OK button).
 *
 * @param {string} message - The message to display
 * @param {Object} [options] - Optional overrides
 * @param {string} [options.title] - Modal title (defaults to 'Alert')
 * @param {string} [options.buttonText='OK'] - Button label
 * @param {string} [options.variant='warning'] - 'danger' | 'warning' | 'success' | 'primary'
 * @param {string} [options.icon] - Phosphor icon class for the header (defaults by variant)
 * @returns {Promise<void>} Resolves when the modal is dismissed
 */
export function showAlertModal(message, options = {}) {
  const variant = options.variant || 'warning'
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.warning

  return new Promise((resolve) => {
    mountGlModalShell({
      idPrefix: 'custom-alert-modal',
      title: options.title || 'Alert',
      headerIcon: options.icon || config.alertIcon,
      headerClass: config.headerClass || '',
      bodyHTML: messageBody(message),
      buttons: [
        { text: options.buttonText || 'OK', className: config.okClass, dismiss: true },
      ],
      onHidden: () => resolve(),
    })
  })
}

/**
 * Show a styled prompt modal — a single text input with Confirm/Cancel.
 * Drop-in replacement for browser-native window.prompt().
 *
 * @param {string} message - Label shown above the input
 * @param {Object} [options] - Optional overrides
 * @param {string} [options.title='Enter a value'] - Modal title
 * @param {string} [options.defaultValue=''] - Pre-filled input value
 * @param {string} [options.placeholder=''] - Input placeholder
 * @param {string} [options.inputType='text'] - Input type attribute (e.g. 'url')
 * @param {string} [options.confirmText='OK'] - Confirm button label
 * @param {string} [options.cancelText='Cancel'] - Cancel button label
 * @param {string} [options.variant='primary'] - 'danger' | 'warning' | 'success' | 'primary'
 * @param {Object} [options.checkbox] - Optional checkbox under the input:
 *   { label: string, checked: boolean }. Providing it CHANGES the resolved
 *   shape (see @returns) — existing string-shaped callers are unaffected.
 * @returns {Promise<string|{value: string, checked: boolean}|null>} Without
 *   `options.checkbox`, resolves the entered string on confirm (may be empty)
 *   or null if cancelled/dismissed — matching window.prompt() semantics. With
 *   `options.checkbox`, resolves { value, checked } on confirm, null otherwise.
 */
export function showPromptModal(message, options = {}) {
  const variant = options.variant || 'primary'
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.primary
  const checkbox = options.checkbox || null

  return new Promise((resolve) => {
    // null until the user confirms — preserves window.prompt's cancel semantics.
    let result = null

    const confirmValue = (shell) => {
      const inputEl = shell.ref('input')
      const checkboxEl = checkbox ? shell.ref('checkbox') : null
      result = checkboxEl ? { value: inputEl.value, checked: checkboxEl.checked } : inputEl.value
      shell.hide()
    }

    mountGlModalShell({
      idPrefix: 'custom-prompt-modal',
      title: options.title || 'Enter a value',
      headerIcon: options.icon || config.headerIcon,
      headerClass: options.headerClass || config.headerClass,
      bodyHTML: ({ refId }) => `
              <label class="form-label gl-modal-text" for="${refId('input')}">${escapeHTML(message)}</label>
              <input class="gl-form-control" id="${refId('input')}">${
                checkbox ? checkboxMarkup(refId('checkbox'), checkbox.label, 'mt-3') : ''
              }`,
      buttons: [
        cancelButton(options.cancelText || 'Cancel'),
        {
          ref: 'confirm',
          text: options.confirmText || 'OK',
          className: config.okClass,
          onClick: confirmValue,
        },
      ],
      onMounted: (shell) => {
        const inputEl = shell.ref('input')

        // Assign user/caller-controlled values via DOM properties rather than
        // interpolating them into the HTML string. textContent-based escaping does
        // NOT encode double-quotes, so an interpolated value="…" could break out of
        // the attribute and inject markup (e.g. a stored tiptap href containing a
        // quote + onfocus). Property assignment bypasses the HTML parser entirely.
        inputEl.type = options.inputType || 'text'
        inputEl.value = options.defaultValue || ''
        inputEl.placeholder = options.placeholder || ''
        if (checkbox) shell.ref('checkbox').checked = Boolean(checkbox.checked)

        shell.on(inputEl, 'keydown', (event) => {
          if (event.key !== 'Enter') return
          event.preventDefault()
          confirmValue(shell)
        })
      },
      onShown: (shell) => {
        const inputEl = shell.ref('input')
        inputEl.focus()
        inputEl.select()
      },
      onHidden: () => resolve(result),
    })
  })
}

/**
 * Show a styled acknowledgement modal — caller-supplied HTML body, a checkbox
 * the user must tick to enable the confirm button, and Confirm/Cancel buttons.
 *
 * Use when the user must actively acknowledge a notice (e.g., a delivery-SLA
 * warning) before continuing. The body is inserted as trusted HTML so links,
 * <strong>, etc. render — sanitize any caller-controlled values upstream.
 *
 * @param {Object} options
 * @param {string} options.title - Modal title (plain text, escaped)
 * @param {string} options.bodyHTML - Body markup, inserted unescaped
 * @param {string} options.checkboxLabel - Label next to the acknowledgement checkbox (plain text)
 * @param {string} [options.confirmText='Continue'] - Confirm button label
 * @param {string} [options.cancelText='Cancel'] - Cancel button label
 * @param {string} [options.confirmIcon='ph ph-check'] - Phosphor icon for confirm button
 * @param {string} [options.variant='warning'] - 'danger' | 'warning' | 'success' | 'primary'
 * @returns {Promise<boolean>} Resolves true if confirmed after checking the box, false otherwise
 */
export function showAcknowledgementModal(options = {}) {
  const variant = options.variant || 'warning'
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.warning
  const bodyHTML = options.bodyHTML || ''
  const checkboxLabel = options.checkboxLabel || 'I understand.'

  return new Promise((resolve) => {
    let userConfirmed = false

    mountGlModalShell({
      idPrefix: 'custom-ack-modal',
      title: options.title || 'Please acknowledge',
      headerIcon: config.headerIcon,
      headerClass: config.headerClass || '',
      bodyHTML: ({ refId }) => `
              <div class="mb-3">${bodyHTML}</div>${checkboxMarkup(refId('checkbox'), checkboxLabel)}`,
      buttons: [
        cancelButton(options.cancelText || 'Cancel'),
        {
          ref: 'confirm',
          text: options.confirmText || 'Continue',
          className: config.actionClass,
          icon: options.confirmIcon || 'ph ph-check',
          disabled: true,
          onClick: (shell) => {
            if (!shell.ref('checkbox').checked) return
            userConfirmed = true
            shell.hide()
          },
        },
      ],
      onMounted: (shell) => {
        const checkboxEl = shell.ref('checkbox')
        shell.on(checkboxEl, 'change', () => {
          shell.buttons.confirm.disabled = !checkboxEl.checked
        })
      },
      onHidden: () => resolve(userConfirmed),
    })
  })
}

// Make available globally for non-module contexts
window.showConfirmModal = showConfirmModal
window.showAlertModal = showAlertModal
window.showPromptModal = showPromptModal
window.showAcknowledgementModal = showAcknowledgementModal
